import React, { useCallback, useEffect, useState } from 'react';
import ProfileIcon from '../../assets/icons/profile.svg';
import logoutIcon from '../../assets/icons/logout.svg';
import { ACCESS_TOKEN, REFRESH_TOKEN } from 'config/keywords';
import { API_BASE_URL } from 'config/constant';
import axiosInstance from 'config/axiosInstance';
import { useTranslation } from 'react-i18next';

interface ProfileType {
  name?: string;
  email?: string;
  role?: string;
  company_info?: {
    name?: string
  };
}

const Profile: React.FC = () => {
  const { t } = useTranslation('layout');
  const [showPopup, setShowPopup] = useState(false);
  const accessToken = localStorage.getItem(ACCESS_TOKEN);
  const [profile, setProfile] = useState<ProfileType | null>(null);

  const handleLogout = async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN);
    if (refreshToken) {
      try {
        const response = await fetch(`${API_BASE_URL}/v1/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${refreshToken}`,
          },
          body: JSON.stringify({ token: refreshToken }),
        });

        if (response.ok) {
          console.log('Logged out successfully');
        } else {
          console.error('Failed to log out', response.statusText);
        }
      } catch (error) {
        console.error('Error logging out', error);
      }
    }
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
    localStorage.removeItem('language');
    window.location.href = '/login';
  };

  const togglePopup = () => setShowPopup(!showPopup);

  const confirmLogout = () => {
    togglePopup();
    handleLogout();
  };

  const fetchData = useCallback(async () => {
    try {
      const apiUrl = '/v1/profile/';
      const response = await axiosInstance.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch profile data', error);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  return (
    <div>
      <div
        className="flex flex-row justify-between bg-slate-200 bg-opacity-20 p-2 rounded-md hover:opacity-75"
        onClick={togglePopup}
      >
        <div className="flex flex-row space-x-2 items-center">
          <img src={ProfileIcon} alt="Profile Icon" className="h-16" />
          <div className="">
            <p
              className="text-body-2 text-primary/50 truncate w-40"
              style={{ maxWidth: '8rem' }}
            >
              {profile?.name || `${t('profile.UNKNOWN')}`}
            </p>
            <p className="text-caption text-primary/50">{t(`profile.${profile?.role}`, { defaultValue: profile?.role })}</p>
            <p className="text-caption text-primary/75">{profile?.company_info?.name}</p>
          </div>
        </div>
        <img src={logoutIcon} alt="Logout" style={{ cursor: 'pointer' }} />
      </div>
      {showPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-75 z-50">
          <div className="bg-white py-6 rounded-lg shadow-lg space-y-4">
            <div className="px-24">
              <h2 className="text-title mb-4">{t('logout.title')}</h2>
            </div>
            <div className="flex flex-col border-y items-center justify-center py-4">
              <p className="px-6">{t('logout.info')}</p>
              <p className="px-6">{t('logout.info2')}</p>
            </div>
            <div className="flex justify-between space-x-4 px-6">
              <button
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-500 rounded hover:bg-gray-100"
                onClick={togglePopup}
              >
                {t('logout.cancel')}
              </button>
              <button
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                onClick={confirmLogout}
              >
                {t('logout.logout')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
